import { withoutScenarioTeams } from './scenarioTeams';
/**
 * data.js — Pitch-state factory functions and scenario migration helpers.
 *
 * This module is the single source of truth for the "shape" of a pitch state,
 * phase, and scenario in the KickViz app.
 *
 * ── Keyframe-based pitch state structure ──
 *
 * A scenario has multiple phases (keyframes). Each phase has a `start` state
 * describing where every item IS at that keyframe. Animation always goes from
 * phase N's start to phase N+1's start.
 *
 * ── Ball model ──
 *
 * The ball has an `attached` field:
 *   null            → independent: ball uses its own position keyframe
 *   'home:3'        → attached: ball follows that player's position + 0.8m offset
 *
 * ── Arc types (transition movement) ──
 *
 * Each item (ball, players, cones, targets) can have an `arc` property:
 *   type: 'basic'   — straight line from A to B
 *   type: 'curve'   — curved path (direction, bend, height)
 *   type: 'manual'  — user-defined waypoints (points array)
 */

/**
 * Migrate a ball object from the old arc-based model to the new `attached` model.
 * - Old: arc.type === 'player' with playerSide/playerIndex
 * - New: attached = 'home:3' (or null), arc.type stays as 'basic'/'curve'/'manual'
 * Also cleans up `fromPlayer` flag and leftover playerSide/playerIndex on arc.
 */
export function migrateBall(ball) {
    if (!ball) return ball;
    const migrated = { ...ball };
    if (!migrated.arc) return migrated;

    // If arc.type === 'player', convert to attached
    if (migrated.arc.type === 'player' && migrated.arc.playerSide != null && migrated.arc.playerIndex != null) {
        migrated.attached = `${migrated.arc.playerSide}:${migrated.arc.playerIndex}`;
        migrated.arc = { ...migrated.arc, type: 'basic', playerSide: undefined, playerIndex: undefined };
    }
    // If arc.fromPlayer with playerSide/playerIndex, convert to attached
    if (migrated.arc.fromPlayer && migrated.arc.playerSide != null && migrated.arc.playerIndex != null) {
        if (!migrated.attached) {
            migrated.attached = `${migrated.arc.playerSide}:${migrated.arc.playerIndex}`;
        }
        migrated.arc = { ...migrated.arc, fromPlayer: undefined, playerSide: undefined, playerIndex: undefined };
    }
    // Clean up any remaining playerSide/playerIndex on arc
    if (migrated.arc.playerSide !== undefined || migrated.arc.playerIndex !== undefined || migrated.arc.fromPlayer !== undefined) {
        const { playerSide, playerIndex, fromPlayer, ...rest } = migrated.arc;
        migrated.arc = rest;
    }
    // Ensure attached field exists
    if (migrated.attached === undefined) migrated.attached = null;
    return migrated;
}

/**
 * Migrate an entire pitch state, converting ball from old to new model.
 */
export function migratePitchState(state) {
    if (!state) return state;
    const result = { ...state };
    // Migrate arc.speed → arc.offset → arc.offsetStart/offsetEnd on all items
    const migrateArc = (item) => {
        if (!item?.arc) return item;
        // Old speed → offset
        if (item.arc.speed !== undefined && item.arc.offset === undefined) {
            item.arc = { ...item.arc, offset: item.arc.speed };
            delete item.arc.speed;
        }
        // Old single offset → offsetStart/offsetEnd range
        if (item.arc.offset !== undefined && item.arc.offsetStart === undefined) {
            item.arc.offsetEnd = item.arc.offset;
            item.arc.offsetStart = 0;
            delete item.arc.offset;
        }
        return item;
    };
    if (result.home?.players) result.home.players = result.home.players.map(migrateArc);
    if (result.away?.players) result.away.players = result.away.players.map(migrateArc);
    if (result.cones) result.cones = result.cones.map(migrateArc);
    if (result.targets) result.targets = result.targets.map(migrateArc);
    if (result.ball) {
        // Old single-ball model: convert to array
        const migrated = migrateBall(migrateArc(result.ball));
        if (!migrated.id) migrated.id = 'ball-1';
        result.balls = [migrated];
        delete result.ball;
    } else if (result.balls) {
        // New multi-ball model: migrate each ball, ensure unique id
        result.balls = result.balls.map((b, i) => {
            const migrated = migrateBall(migrateArc(b));
            if (!migrated.id) migrated.id = `ball-${i + 1}`;
            return migrated;
        });
        delete result.ball;
    } else {
        // No ball field at all — initialise empty array
        result.balls = [];
        delete result.ball;
    }
    // Migrate zones: ensure mode, closed, thickness, pointRefs, radius fields exist
    if (result.zones) {
        result.zones = result.zones.map(z => ({
            ...z,
            mode: z.mode || 'static',
            closed: z.closed !== undefined ? z.closed : true,
            thickness: z.thickness ?? 2,
            pointRefs: z.pointRefs || [],
            radius: z.radius ?? 3, // circle zones
        }));
    }
    return result;
}

/**
 * Migrate an entire scenario, converting all phases' ball data.
 */
export function migrateScenario(scenario) {
    scenario = withoutScenarioTeams(scenario);
    if (!scenario || !scenario.phases) return scenario;
    return {
        ...scenario,
        schemaVersion: scenario.schemaVersion || '1.0',
        keyframes: scenario.keyframes || [],
        phases: scenario.phases.map(p => {
            const migrated = {
                ...p,
                duration: p.duration ?? 3,
                start: migratePitchState(p.start),
            };
            // Legacy transition field — kept for backward compat but no longer used
            if (p.transition) migrated.transition = migratePitchState(p.transition);
            return migrated;
        }),
    };
}

/**
 * Create a fresh empty pitch state.
 */
export function createEmptyPitchState() {
    return {
        balls: [],
        cones: [],
        targets: [],
        zones: [],
        home: { players: [] },
        away: { players: [] },
    };
}

/**
 * Create a new phase with empty start state.
 */
export function createEmptyPhase(phaseNum = 1) {
    return {
        name: `Phase ${phaseNum}`,
        duration: 3,
        start: createEmptyPitchState(),
    };
}

/**
 * Create a new scenario with a single default phase.
 */
export function createEmptyScenario(name = 'New Scenario') {
    return {
        schemaVersion: '2.0',
        id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        name,
        phases: [createEmptyPhase(1)],
        // V2 keyframe tracks. Each track: { id, type, side?, index?, position: [{ time, position }] }
        // "position" is the movement track (whole-second keyframe times).
        keyframes: [],


    };
}

// ─────────────────────────────────────────────────────────────
// SEGMENTS — per-pair transition properties between movement keys.
// A segment lives on its track as { from, to, style, curve, arch, archPeak }
// where from/to are the adjacent position-key times (whole seconds).
//   style:     'straight' | 'curveLeft' | 'curveRight'  — lateral bend
//   curve:     0..1   — how big the lateral bend is
//   arch:      0..10  — extra height the ball reaches (players ignore it)
//   archPeak:  0..1   — where along the path the arch peaks (early/mid/late)
// NOTE: the arrival height of a transition lives on the END KEYFRAME's
// position[1] (edited via the transition GUI's Height slider), so a
// transition always begins from the previous transition's end height.
// ─────────────────────────────────────────────────────────────

export const DEFAULT_SEGMENT = { style: 'straight', curve: 0.5, arch: 0, archPeak: 0.5 };

/**
 * Whether a ball is drawn on the pitch.
 *
 * `visible` is optional on a ball: scenarios saved before the flag existed have
 * no value at all, and `undefined`/`null` must keep meaning "visible" so old
 * scenarios keep showing their ball. Only an explicit `false` hides it.
 *
 * @param {object} ball - ball state ({ id, color, position, attached, visible })
 * @returns {boolean} true when the ball should be drawn
 */
export function ballIsVisible(ball) {
    return ball?.visible !== false;
}

/**
 * Value for a NEW zone-visual key.
 *
 * The shape (points / refs / centre) comes from the snapshot taken at the
 * playhead; the LOOK is inherited from the previous visual key when one exists
 * (the window the playhead is currently in) so a new key continues the zone's
 * current appearance instead of snapping back to the zone's base properties.
 * Only the first key of a zone — no previous key — falls back to the base zone.
 *
 * `opacity: 0` is a valid look: it hides the zone for that key's window.
 *
 * @param {object} p
 * @param {object|null} p.inherited Previous visual key's `value`, or null.
 * @param {object|null} p.zone Base zone from the phase state.
 * @param {Array} p.points Snapshot of the displayed shape (world space).
 * @param {Array} p.pointRefs Snapshot of the vertex attachments.
 * @param {Array} p.center Centre the snapshot was captured against.
 * @returns {object} The new key's value.
 */
export function buildZoneVisualKeyValue({ inherited, zone, points, pointRefs, center }) {
    return {
        radius: inherited?.radius ?? zone?.radius ?? 3,
        points,
        pointRefs,
        closed: inherited?.closed ?? zone?.closed ?? true,
        color: inherited?.color ?? zone?.color ?? '#ffeb3b',
        opacity: inherited?.opacity ?? zone?.opacity ?? 0.35,
        thickness: inherited?.thickness ?? zone?.thickness ?? 2,
        center,
    };
}

/** Segment config for the pair (from, to), or the neutral defaults. */
export function findSegment(track, from, to) {
    const seg = (track?.segments || []).find(s => s.from === from && s.to === to);
    return seg || DEFAULT_SEGMENT;
}

/**
 * The GRAPHIC key in force at time `t` for one track — the arrow window the
 * playhead is sitting inside. Graphic keys are windows: the key at or before
 * `t` decides what is visible AT `t`, so "are the arrows showing?" is one
 * question with one answer, asked by the G chord, the item menu's ➜ entry and
 * the menu's own label. Returns the key, or null before the first key.
 */
export function graphicKeyAt(tracks, trackId, t) {
    const track = (tracks || []).find(k => k.id === trackId);
    const keys = [...(track?.graphic || [])].filter(k => typeof k?.time === 'number')
        .sort((a, b) => a.time - b.time);
    return keys.filter(k => k.time <= t).pop() || null;
}

/** True when a movement arrow is VISIBLE at `t` for that track. */
export function arrowOnAt(tracks, trackId, t) {
    const key = graphicKeyAt(tracks, trackId, t);
    return !!key && key.value === 'on';
}

/**
 * The ATTACH value in force at `t` for one track — who this item is FOLLOWING
 * at that instant, or null when it stands on its own ('none', no key, no
 * track). Attach keys are windows like graphic keys: the key at or before `t`
 * decides. The attach prompt asks this to title itself honestly, and the ✚
 * group key asks it to skip members that ride a carrier.
 */
export function attachRefAt(tracks, trackId, t) {
    const track = (tracks || []).find(k => k.id === trackId);
    const keys = [...(track?.attach || [])].filter(k => typeof k?.time === 'number')
        .sort((a, b) => a.time - b.time);
    // The epsilon matters: a playhead can sit at 3.9999 while the key is at 4.
    const key = keys.filter(k => k.time <= t + 1e-6).pop();
    return key && key.value && key.value !== 'none' ? key.value : null;
}

/**
 * A readable name for a TRACK ID — 'home:3' → 'Home 4', 'cone:cone-01' →
 * 'Cone 01'. The id is the only thing a cross-track answer carries (the sync
 * verb names WHICH other item's moment it is landing on), so the wording lives
 * in one place rather than being re-spelled by every toast.
 *
 * Pass `words` — a { home, away, ball, cone, target, zone } map of the coach's
 * OWN words — and this function stops being a second, English copy of the item
 * names the dictionary already owns. Without it the English fallback stands, so
 * the pure data layer stays usable in tests and in a node context.
 *
 * The fallback is a fallback, not a feature: every user-facing call site in App
 * passes the map. A sentence like "lands on the same second as Cone 3" in a
 * Portuguese app is the same bug as an untranslated button.
 */
export function trackDisplayLabel(tid, words) {
    const [kind, id] = String(tid || '').split(':');
    const w = words || {};
    if (!kind) return w.other || 'another item';   // a junk id must still read as a sentence
    if (kind === 'home' || kind === 'away') {
        return `${w[kind] || (kind === 'home' ? 'Home' : 'Away')} ${Number(id) + 1}`;
    }
    const name = w[kind] || { ball: 'Ball', cone: 'Cone', target: 'Target', zone: 'Zone' }[kind] || kind;
    return id ? `${name} ${String(id).split('-').slice(1).join('-') || id}` : name;
}

/**
 * The ACTION key in force at `t` for one track — the last action key at or
 * before the playhead, or null. An action key is a one-shot TRIGGER, but the
 * pose it leaves the character in carries on, so "what is this player doing
 * right now" is the same window question as arrows and attachments.
 * The action menu asks it twice: to mark the option that is live NOW, and to
 * refuse re-writing the identical key at the identical instant (a no-op the
 * toast would have to lie about).
 */
export function actionKeyInForce(tracks, trackId, t) {
    const track = (tracks || []).find(k => k.id === trackId);
    const keys = [...(track?.action || [])].filter(k => typeof k?.time === 'number')
        .sort((a, b) => a.time - b.time);
    return keys.filter(k => k.time <= t + 1e-6).pop() || null;
}

/**
 * Jump navigation: the time of the nearest keyframe to `t` in direction `dir`
 * (-1 = previous, +1 = next), across every key type (position/action/graphic/
 * attach/visual). With `trackId` only that track is searched; without it, all
 * tracks. Keys sitting exactly at `t` are skipped, so repeated presses walk
 * the sequence. Returns null when there is no key in that direction.
 */
export function nearestKeyTime(tracks, t, dir, trackId = null) {
    let best = null;
    for (const track of tracks || []) {
        if (trackId && track.id !== trackId) continue;
        for (const type of KEY_ARRAYS) {
            for (const k of track[type] || []) {
                if (typeof k?.time !== 'number' || !Number.isFinite(k.time)) continue;
                const d = (k.time - t) * dir;
                if (d > 0.001 && (best === null || d < best)) best = d;
            }
        }
    }
    return best === null ? null : t + dir * best;
}

/**
 * Interpolate between two position keys k0 → k1 at fraction f (0..1) following
 * the segment's trajectory: linear base + optional lateral curve + optional
 * arch (peak height at archPeak fraction). Returns [x, y, z].
 *
 * Both the lateral bend and the arch use plain sine envelopes (NO easing):
 * the curve is at full slope right out of the keyframe, so each segment is a
 * clean arc and the direction change at a keyframe is a sharp corner.
 */
export function interpolateWithSegment(k0, k1, f, seg) {
    const s = seg || DEFAULT_SEGMENT;
    const p0 = k0.position || [0, 0, 0];
    const p1 = k1.position || [0, 0, 0];
    let x = p0[0] + (p1[0] - p0[0]) * f;
    let z = p0[2] + (p1[2] - p0[2]) * f;

    // Lateral curve: perpendicular bend, zero at both ends, max mid-way.
    // The bend is PROPORTIONAL to the segment length (max 25% of the distance),
    // so a curve is clearly visible no matter how long the movement is.
    const style = s.style || 'straight';
    if (style !== 'straight') {
        const dx = p1[0] - p0[0], dz = p1[2] - p0[2];
        const len = Math.hypot(dx, dz);
        if (len > 0.001) {
            const side = style === 'curveLeft' ? -1 : 1;
            const bend = Math.sin(f * Math.PI) * (s.curve ?? 0.5) * 0.25 * len;
            x += (-dz / len) * bend * side;
            z += (dx / len) * bend * side;
        }
    }

    // Height: slides linearly between the two keys' REAL heights (their
    // position[1]) — the end key's y is set by the transition GUI's Height
    // slider, so a transition always starts from the previous one's end
    // height. The arch (if any) is extra height ABOVE that baseline,
    // peaking at archPeak. So:
    //   flat keys, arch 0   → flat move (ground, as before)
    //   end key y=2, arch 0 → rises linearly, arrives 2 m up
    //   end key y=2, arch 3 → rises to ~5 m mid-flight, lands at 2 m
    const arch = s.arch ?? 0;
    const startY = p0[1] ?? 0;
    const endY = p1[1] ?? 0;
    const baseY = startY + (endY - startY) * f;
    let height = baseY;
    if (arch > 0) {
        const peak = Math.min(0.95, Math.max(0.05, s.archPeak ?? 0.5));
        const maxY = startY + arch; // arch is extra height above the start
        let env; // 0 → 1 → 0 envelope
        if (f <= peak) env = Math.sin((f / peak) * (Math.PI / 2));
        else env = Math.cos(((f - peak) / (1 - peak)) * (Math.PI / 2));
        height = baseY + (maxY - baseY) * Math.max(0, env);
    }
    return [x, height, z];
}

// ─────────────────────────────────────────────────────────────
// TIMELINE LENGTH
// Every key kind a track can hold. A branch's timeline is as long as its
// latest key of ANY kind — movement keys are the usual answer, but a ball
// attach, a graphic window or a zone visual can legitimately sit past the last
// movement key, and "Clip time" must not cut those off.
// ─────────────────────────────────────────────────────────────
export const KEY_ARRAYS = ['position', 'action', 'attach', 'graphic', 'visual'];

/**
 * The time of the LAST keyframe held by a branch, across every track and every
 * key kind. 0 when the branch holds nothing.
 *
 * This is what "Clip time" uses: it sets the branch's timeline length to
 * exactly this moment, so the timeline ends where the animation ends instead of
 * trailing empty seconds (or cutting off keys that sit past the current end).
 *
 * @param {Array} keyframes  the branch's tracks
 * @returns {number} seconds
 */
export function lastKeyTime(keyframes) {
    let last = 0;
    for (const track of keyframes || []) {
        if (!track) continue;
        for (const kind of KEY_ARRAYS) {
            const keys = track[kind];
            if (!Array.isArray(keys)) continue;
            for (const k of keys) {
                const t = Number(k && k.time);
                if (Number.isFinite(t) && t > last) last = t;
            }
        }
    }
    return last;
}

/**
 * The subActions a number key can answer, in the order they are NUMBERED.
 *
 * A prompt may carry UTILITY options — verbs that belong to the same object but
 * are not the answer the prompt is asking (⇄ Mirror sits at the top of the
 * action picker). Those must not steal the 1–9 mapping: the picker's "1 = my
 * favourite action" muscle memory is the whole point of ranking it, and a
 * utility entry has no number on its button either. ONE copy of that rule, used
 * by the renderer and the key handler alike, so the number printed on a button
 * is always the key that fires it.
 *
 * @param {object|null} prompt
 * @returns {Array} the numbered subActions
 */
export function promptNumberedOptions(prompt) {
    return ((prompt && prompt.subActions) || []).filter(sa => !sa.utility);
}

/**
 * Number-key answering for the central prompt: while a prompt is open, keys
 * 1–9 fire the matching subAction and 0 (or the cancel button) dismisses it.
 *
 * @param {object|null} prompt  the open prompt ({ subActions, cancelAction })
 * @param {string} key          the pressed key
 * @returns {{ run: Function } | null} the action to run, or null when the key
 *          is not a digit, no prompt is open, or the digit has no option.
 */
export function promptKeyAction(prompt, key) {
    if (!prompt || !/^[0-9]$/.test(key || '')) return null;
    const n = +key;
    if (n === 0) return prompt.cancelAction ? { run: prompt.cancelAction } : null;
    const sa = promptNumberedOptions(prompt)[n - 1];
    return sa && typeof sa.action === 'function' ? { run: sa.action } : null;
}

/**
 * Order the action picker by what THIS scenario actually uses: the most-keyed
 * actions float to the top, so the number-key shortcut (1 = first option)
 * lands on the one you reach for most. Ties keep the default catalogue order
 * (Array.sort is stable), and an untouched scenario gets the catalogue as-is.
 *
 * @param {Array<{value:string}>} types  the action catalogue
 * @param {Array} keyframes              the branch's tracks
 * @returns {Array} a newly sorted array (input never mutated)
 */
export function rankActions(types, keyframes) {
    const counts = new Map();
    for (const track of keyframes || []) {
        for (const k of track?.action || []) {
            if (k && k.value != null) counts.set(k.value, (counts.get(k.value) || 0) + 1);
        }
    }
    return [...(types || [])].sort((a, b) => (counts.get(b.value) || 0) - (counts.get(a.value) || 0));
}

/**
 * TIME-SCALE a branch: multiply EVERY key time of every kind (and every
 * segment span) by a factor, snapped back to the 0.25 s grid. "Same move,
 * played slower/faster" without touching a single keyframe.
 *
 * Rules: times never go negative; keys that collide on the same snapped time
 * within one array collapse to the EARLIER key (the state that was reached
 * first wins); segments whose span collapses (from >= to) are dropped;
 * segments ride along so curved paths keep their shape.
 *
 * @param {Array} keyframes  the branch's tracks
 * @param {number} factor    e.g. 1.25 to stretch, 0.8 to compress
 * @returns {Array} new tracks (input never mutated)
 */
export function scaleKeyTimes(keyframes, factor) {
    const f = Number(factor);
    if (!Number.isFinite(f) || f <= 0) return keyframes;
    const snap = t => Math.round(Math.max(0, t * f) * 4) / 4;
    return (keyframes || []).map(track => {
        if (!track) return track;
        const out = { ...track };
        for (const kind of KEY_ARRAYS) {
            if (!Array.isArray(track[kind])) continue;
            const seen = new Set();
            const keys = [];
            for (const k of [...track[kind]].sort((a, b) => (Number(a?.time) || 0) - (Number(b?.time) || 0))) {
                const t = snap(Number(k?.time) || 0);
                if (seen.has(t)) continue;
                seen.add(t);
                keys.push({ ...k, time: t });
            }
            out[kind] = keys;
        }
        if (Array.isArray(track.segments)) {
            out.segments = track.segments
                .map(s => ({ ...s, from: snap(Number(s?.from) || 0), to: snap(Number(s?.to) || 0) }))
                .filter(s => s.from < s.to);
        }
        return out;
    });
}

/**
 * RIPPLE MOVE — the ⌥-drag twin of [ / ].
 *
 * A plain key drag moves ONE moment and leaves a hole behind it. A ripple move
 * also opens or closes the gap: every key AT the cut `from` lands at `to`, and
 * everything STRICTLY AFTER it — in every track, every lane, and every segment
 * boundary — slides by the same delta. Keys before the cut never move.
 *
 * The cut is uniform across tracks on purpose: a pass catch is a position key
 * in one track and a ball key at the same instant in another, and a ripple
 * that left one behind would split the moment (and collide on the pull-back).
 *
 * Times stay on the 0.25 s grid — the drag already snaps `to`, and a uniform
 * delta keeps distinct grid times distinct, so no two keys can collapse.
 *
 * @param {Array}  keyframes  the branch's tracks
 * @param {number} from       the cut — the grabbed moment's current time
 * @param {number} to         where the drag drops it
 * @returns {Array} new tracks (input never mutated)
 */
export function rippleMoveTracks(keyframes, from, to) {
    const EPS = 1e-6;
    const f = Number(from), t2 = Number(to);
    if (!Number.isFinite(f) || !Number.isFinite(t2)) return keyframes;
    const delta = t2 - f;
    const grid = (t) => Math.round(t * 4) / 4;
    return (keyframes || []).map(track => {
        if (!track) return track;
        const out = { ...track };
        const move = (v) => {
            if (!Number.isFinite(v)) return v;
            if (Math.abs(v - f) < EPS) return t2;
            if (v > f + EPS) return grid(v + delta);
            return v;
        };
        for (const kind of KEY_ARRAYS) {
            if (!Array.isArray(track[kind])) continue;
            out[kind] = track[kind]
                .map(k => (Number.isFinite(Number(k?.time)) ? { ...k, time: move(Number(k.time)) } : k))
                .sort((a, b) => (Number(a?.time) || 0) - (Number(b?.time) || 0));
        }
        if (Array.isArray(track.segments)) {
            out.segments = track.segments
                .map(s => ({ ...s, from: move(Number(s?.from)), to: move(Number(s?.to)) }))
                .filter(s => (Number(s?.from) || 0) < (Number(s?.to) || 0));
        }
        return out;
    });
}

/**
 * MIRROR — flip tracks across the pitch, animation included.
 *
 * axis 2 (z) = flank ↔ flank: the touchline-to-touchline mirror, the one a
 * coach reaches for ("run this move down the other side").
 * axis 0 (x) = end ↔ end: across the halfway line, your half ↔ theirs.
 * (The pitch is centred on the origin, home on the left — see the navigation
 * tutorial — so negating a coordinate IS the mirror; no offset needed.)
 *
 * What travels with the position keys:
 *  · a zone's shape `points` — they are stored RELATIVE to the zone's centre,
 *    so flipping them keeps the shape attached to the mirrored centre;
 *  · movement BENDS — curveLeft ↔ curveRight, so an arc that bowed toward the
 *    far touchline still bows toward the far touchline after the flip. Bending
 *    is a side decision, not a number, so it has to be swapped explicitly.
 *
 * Attachments (attach lane, vertex refs) are by ITEM ID and survive: mirroring
 * a player and the ball keeps the ball attached to that player.
 *
 * @param {Array}  keyframes  the branch's tracks
 * @param {Array}  trackIds   which tracks to flip ('home:3', 'ball:ball-01', …)
 * @param {number} axis       0 = x · 2 = z
 * @returns {Array} new tracks (input never mutated)
 */
export function mirrorTracks(keyframes, trackIds, axis) {
    const ids = new Set(trackIds || []);
    if (!ids.size || (axis !== 0 && axis !== 2)) return keyframes;
    // `v === 0 ? 0 : -v` — a plain -v turns a point sitting ON the mirror line
    // into -0, which survives JSON as 0 but breaks equality checks and any
    // later `p === 0` comparison in the app.
    const flip = (p) => (Array.isArray(p) && p.length > axis
        ? p.map((v, i) => (i === axis ? (v === 0 ? 0 : -v) : v))
        : p);
    return (keyframes || []).map(track => {
        if (!track || !ids.has(track.id)) return track;
        const out = { ...track };
        if (Array.isArray(track.position)) {
            out.position = track.position.map(k => (k && Array.isArray(k.position)
                ? { ...k, position: flip(k.position) }
                : k));
        }
        if (Array.isArray(track.visual)) {
            out.visual = track.visual.map(k => {
                const pts = k?.value?.points;
                if (!Array.isArray(pts)) return k;
                return { ...k, value: { ...k.value, points: pts.map(flip) } };
            });
        }
        if (Array.isArray(track.segments)) {
            out.segments = track.segments.map(s => {
                if (s?.style === 'curveLeft') return { ...s, style: 'curveRight' };
                if (s?.style === 'curveRight') return { ...s, style: 'curveLeft' };
                return s;
            });
        }
        return out;
    });
}

/**
 * The height STEP, written once: the 0-10 m clamp and the direction. The
 * single-item path and the group path of the height chord (and the item menu's
 * up / down entries) all call this, so a ceiling can never be raised in one
 * door and left standing in the other.
 */
export function nextHeight(cur, dir, step) {
    return Math.max(0, Math.min(10, cur + (dir > 0 ? step : -step)));
}

/**
 * The BEND cycle, written once: straight → left → right → straight, whichever
 * way it was reached. The single-item verb and the group verb both call this,
 * so the ring cannot gain a fourth stop in one door and not the other.
 */
export function nextBendStyle(style) {
    return style === 'straight' ? 'curveLeft'
        : style === 'curveLeft' ? 'curveRight' : 'straight';
}

/**
 * How long a toast stays up — written once, next to the other shared rules.
 *
 * Every sentence the app speaks on the touch path is an announcement: the
 * result of a gesture plus what to do next. "Grabbed vertex 1 of 2 — drag it
 * on the pitch · + Add puts another · − Remove takes one away" is 90 characters
 * of 13px type on a phone, and a flat three seconds is not enough time to read
 * it, let alone act on it. Announcing something nobody can finish reading is a
 * fake announcement.
 *
 * Short news still leaves fast; long news gets the time its length needs.
 */
export function toastLife(message) {
    const len = (message || '').length;
    if (len <= 30) return 3000;
    return Math.min(8000, 3000 + (len - 30) * 55);
}
